import { useState, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { FileExplorerItem } from '@features/file-explorer/types'
import { AppliedFilters } from '@features/file-explorer/components/Toolbar/filterTypes'
import { savePreferences } from '@utils/preferences'

type LoadingElement =
  | 'sort-field'
  | 'sort-direction'
  | 'pagination-prev'
  | 'pagination-next'
  | 'per-page'
  | 'filter'
  | 'folder-navigation'
  | null

interface UseFileExplorerNavigationProps {
  current_folder_id: string | null
  sort_by: string
  sort_direction: 'asc' | 'desc'
  filters: AppliedFilters
  page: number
  per_page: number
}

export function useFileExplorerNavigation({
  current_folder_id,
  sort_by,
  sort_direction,
  filters,
  page,
  per_page
}: UseFileExplorerNavigationProps) {
  const [loadingElement, setLoadingElement] = useState<LoadingElement>(null)
  const [loadingFolderId, setLoadingFolderId] = useState<string | null>(null)

  const getUrlForFolder = (folderId: string | null) => {
    return folderId ? `/${folderId}` : '/'
  }

  // Centralized navigation method
  const navigate = useCallback((params: {
    sort_by?: string
    sort_direction?: 'asc' | 'desc'
    filters?: Record<string, any>
    page?: number
    per_page?: number
  }, element: LoadingElement = null) => {
    // Show loading state immediately
    setLoadingElement(element)

    // Build query parameters for URL
    const queryParams = {
      sort_by: params.sort_by ?? sort_by,
      sort_direction: params.sort_direction ?? sort_direction,
      filters: params.filters ?? filters,
      page: params.page ?? page,
      per_page: params.per_page ?? per_page
    }

    router.get(getUrlForFolder(current_folder_id), queryParams, {
      except: ['breadcrumbs', 'folder_tree', 'storage_usage'],
      preserveState: true,
      preserveScroll: true,
      replace: true,
      onFinish: () => {
        setLoadingElement(null)
      }
    })
  }, [current_folder_id, sort_by, sort_direction, filters, page, per_page])

  const handleSortChange = useCallback((field: string) => {
    const newDirection = sort_by === field && sort_direction === 'asc' ? 'desc' : 'asc'
    // Save preferences to cookie
    savePreferences({ sort_by: field, sort_direction: newDirection })
    navigate({ sort_by: field, sort_direction: newDirection }, 'sort-field')
  }, [sort_by, sort_direction, navigate])

  const handleDirectionToggle = useCallback(() => {
    const newDirection = sort_direction === 'asc' ? 'desc' : 'asc'
    // Save preferences to cookie
    savePreferences({ sort_direction: newDirection })
    navigate({ sort_direction: newDirection }, 'sort-direction')
  }, [sort_direction, navigate])

  const handlePageChange = useCallback((newPage: number, direction: 'prev' | 'next') => {
    navigate({ page: newPage }, direction === 'prev' ? 'pagination-prev' : 'pagination-next')
  }, [navigate])

  const handleApplyFilters = useCallback((newFilters: Record<string, any>) => {
    navigate({ filters: newFilters, page: 1 }, 'filter')
  }, [navigate])

  const handlePerPageChange = useCallback((newPerPage: number) => {
    // Save preferences to cookie
    savePreferences({ per_page: newPerPage })
    navigate({ per_page: newPerPage, page: 1 }, 'per-page')
  }, [navigate])

  const handleFolderNavigation = useCallback((folderId: string | null) => {
    // Use '__root__' as a sentinel value to indicate loading root (null folder)
    setLoadingFolderId(folderId === null ? '__root__' : folderId)

    // Show loading state immediately
    setLoadingElement('folder-navigation')

    const url = folderId ? `/${folderId}` : '/'
    router.get(url, {}, {
      except: ['folder_tree', 'storage_usage'],
      onFinish: () => {
        setLoadingFolderId(null)
        setLoadingElement(null)
      }
    })
  }, [])

  const handleItemClick = useCallback((item: FileExplorerItem) => {
    if (item.is_folder) {
      // Navigate into folder with loading state
      handleFolderNavigation(item.id)
    } else {
      // Navigate to document preview
      router.get(`/documents/${item.id}/preview`)
    }
  }, [handleFolderNavigation])

  return {
    loadingElement,
    loadingFolderId,
    handleSortChange,
    handleDirectionToggle,
    handlePageChange,
    handleApplyFilters,
    handlePerPageChange,
    handleFolderNavigation,
    handleItemClick
  }
}
